import React, { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { keepPreviousData, useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import {
  AlertCircle,
  ArrowLeft,
  ArrowRight,
  Calendar,
  CreditCard,
  RefreshCw,
  ShieldOff,
  Wallet,
} from "lucide-react";
import { toast } from "sonner";

import KpiCard from "@/components/common/KpiCard";
import PageHeader from "@/components/common/PageHeader";
import PageSearchInput from "@/components/common/PageSearchInput";
import StatusBadge from "@/components/common/StatusBadge";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { Button } from "@/components/ui/button";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Skeleton } from "@/components/ui/skeleton";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { Textarea } from "@/components/ui/textarea";
import { useApp } from "@/context/AppContext";
import {
  assignShopSubscriptionPlan,
  grantShopSubscriptionAccess,
  listSubscriptionShops,
  revokeShopSubscriptionAccess,
} from "@/lib/api";
import { daysUntil, fmtDate } from "@/lib/formatters";
import { formatMoneyMinor, formatMoneyTotals } from "@/pages/overviewModel";

const PAGE_SIZE = 40;

function SubscriptionsSkeleton() {
  return (
    <div className="space-y-6" role="status" aria-label="Loading subscriptions">
      <span className="sr-only">Loading subscriptions</span>
      <div className="grid grid-cols-2 gap-4 md:grid-cols-4">
        {Array.from({ length: 4 }, (_, index) => <Skeleton key={index} className="h-[116px] rounded-xl" />)}
      </div>
      <Skeleton className="h-11 w-full max-w-3xl rounded-xl" />
      <div className="card-elev overflow-hidden rounded-xl">
        {Array.from({ length: 7 }, (_, index) => (
          <div key={index} className="flex h-[72px] items-center gap-5 border-b border-[#0E5B52]/8 px-4">
            <div className="w-56 space-y-2"><Skeleton className="h-4 w-36" /><Skeleton className="h-3 w-24" /></div>
            <Skeleton className="h-9 w-40 rounded-lg" />
            <Skeleton className="h-6 w-20 rounded-full" />
            <Skeleton className="ml-auto h-8 w-32 rounded-lg" />
          </div>
        ))}
      </div>
    </div>
  );
}

export default function Subscriptions() {
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const { currentAdmin } = useApp();
  const [tab, setTab] = useState("all");
  const [search, setSearch] = useState("");
  const [cursorStack, setCursorStack] = useState([""]);
  const [page, setPage] = useState(0);
  const [action, setAction] = useState(null);
  const [reason, setReason] = useState("");
  const debouncedSearch = search.trim();
  const canOperate = currentAdmin?.permissions?.includes("subscriptions.manage");

  useEffect(() => {
    setCursorStack([""]);
    setPage(0);
  }, [debouncedSearch, tab]);

  const filters = useMemo(() => ({
    cursor: cursorStack[page] || "",
    limit: PAGE_SIZE,
    query: debouncedSearch,
    status: tab,
  }), [cursorStack, debouncedSearch, page, tab]);

  const { data, error, isError, isFetching, isLoading, refetch } = useQuery({
    queryKey: ["admin-subscriptions", filters],
    queryFn: ({ signal }) => listSubscriptionShops(filters, signal),
    placeholderData: keepPreviousData,
  });

  const items = data?.items || [];
  const metrics = data?.metrics || {};
  const counts = metrics.counts || {};
  const plansByCurrency = useMemo(() => (
    (data?.plans || []).reduce((grouped, plan) => {
      grouped[plan.currencyCode] = [...(grouped[plan.currencyCode] || []), plan];
      return grouped;
    }, {})
  ), [data?.plans]);

  const refreshRelated = async (shopId) => {
    await Promise.all([
      queryClient.invalidateQueries({ queryKey: ["admin-subscriptions"] }),
      queryClient.invalidateQueries({ queryKey: ["admin-overview"] }),
      queryClient.invalidateQueries({ queryKey: ["admin-shops"] }),
      queryClient.invalidateQueries({ queryKey: ["admin-shop", shopId] }),
    ]);
  };

  const mutation = useMutation({
    mutationFn: async ({ type, shopId, planCode }) => {
      if (type === "plan") return assignShopSubscriptionPlan(shopId, planCode, reason);
      if (type === "grant") return grantShopSubscriptionAccess(shopId, { days: 30, reason });
      return revokeShopSubscriptionAccess(shopId, { reason });
    },
    onSuccess: async (_, variables) => {
      await refreshRelated(variables.shopId);
      toast.success(
        variables.type === "plan"
          ? `Plan changed to ${variables.planName}`
          : variables.type === "grant"
            ? "Access extended by 30 days"
            : "Subscription access revoked",
      );
      setAction(null);
      setReason("");
    },
    onError: (mutationError) => toast.error(mutationError.message || "Subscription update failed"),
  });

  const nextPage = () => {
    if (!data?.nextCursor) return;
    setCursorStack((current) => [...current.slice(0, page + 1), data.nextCursor]);
    setPage((current) => current + 1);
  };

  const headerActions = (
    <div className="flex w-full gap-2 md:w-auto">
      <PageSearchInput className="flex-1 md:w-72" value={search} onSearch={setSearch} placeholder="Search subscriptions…" inputClassName="rounded-xl" />
      <Button variant="outline" className="h-10 rounded-xl border-[#0E5B52]/15" disabled={isFetching} onClick={() => refetch()} aria-label="Refresh subscriptions">
        <RefreshCw className={`h-4 w-4 ${isFetching ? "animate-spin" : ""}`} />
      </Button>
    </div>
  );

  if (isLoading) {
    return (
      <div data-testid="subscriptions-page">
        <PageHeader eyebrow="Revenue" title="Subscriptions" subtitle="Manage plan assignments, renewals, grace periods and revenue across all shops." actions={headerActions} />
        <SubscriptionsSkeleton />
      </div>
    );
  }

  const tabs = [
    { value: "all", label: "All", count: counts.all },
    { value: "active", label: "Active", count: counts.active },
    { value: "trial", label: "Trial", count: counts.trial },
    { value: "grace", label: "Grace", count: counts.grace },
    { value: "requires_payment", label: "Need payment", count: counts.requiresPayment },
    { value: "revoked", label: "Revoked", count: counts.revoked },
  ];

  return (
    <div data-testid="subscriptions-page">
      <PageHeader
        eyebrow="Revenue"
        title="Subscriptions"
        subtitle="Manage plan assignments, renewals, grace periods and revenue across all shops."
        actions={headerActions}
      />

      <div className="mb-6 grid grid-cols-2 gap-4 md:grid-cols-4">
        <KpiCard testid="kpi-mrr" label="Paid MRR" value={currentAdmin?.permissions?.includes("financials.view") ? formatMoneyTotals(metrics.paidMrr || []) : "Restricted"} icon={Wallet} accent="#A7F3D0" hint="Paid active subscriptions only" />
        <KpiCard testid="kpi-active-subs" label="Active access" value={counts.active || 0} icon={CreditCard} accent="#D9F99D" />
        <KpiCard testid="kpi-expiring-7d" label="Expiring 7d" value={metrics.expiring7d || 0} icon={Calendar} accent="#FDE047" hint="Includes grants and trials" />
        <KpiCard testid="kpi-expired-subs" label="Need payment" value={counts.requiresPayment || 0} icon={AlertCircle} accent="#FEE2E2" hint="Blocked until renewal or grant" />
      </div>

      {isError && (
        <div className="mb-5 flex items-center justify-between rounded-xl border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-800" role="alert">
          <span>{error?.message || "Could not load subscriptions."}</span>
          <Button variant="ghost" size="sm" onClick={() => refetch()}>Retry</Button>
        </div>
      )}

      <Tabs value={tab} onValueChange={setTab}>
        <TabsList className="h-auto flex-wrap rounded-xl border border-[#0E5B52]/10 bg-white p-1">
          {tabs.map((entry) => (
            <TabsTrigger key={entry.value} value={entry.value} className="rounded-lg data-[state=active]:bg-[#0E5B52] data-[state=active]:text-white">
              {entry.label} ({entry.count || 0})
            </TabsTrigger>
          ))}
        </TabsList>

        <TabsContent value={tab} className="mt-5">
          <div className={`card-elev overflow-hidden rounded-xl transition-opacity ${isFetching ? "opacity-70" : ""}`}>
            <Table>
              <TableHeader>
                <TableRow className="bg-[#F0F6F1]/60 hover:bg-[#F0F6F1]/60">
                  <TableHead>Shop</TableHead><TableHead>Plan</TableHead><TableHead>Status</TableHead>
                  <TableHead>Access until</TableHead><TableHead>MRR</TableHead><TableHead className="text-right">Actions</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {items.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={6} className="p-4">
                      <div className="flex h-44 items-center justify-center rounded-xl bg-[#F0F6F1]/60 text-center text-sm text-[#6B7280]">
                        {counts.all === 0 ? "No subscription records found yet." : "No subscriptions match this search or status."}
                      </div>
                    </TableCell>
                  </TableRow>
                ) : items.map((item) => {
                  const rowPlans = plansByCurrency[item.billingCurrencyCode] || [];
                  const until = daysUntil(item.accessUntil);
                  const isBusy = mutation.isPending && action?.shopId === item.shopId;
                  return (
                    <TableRow key={item.shopId}>
                      <TableCell>
                        <button className="text-left font-semibold text-[#064E3B] hover:underline" onClick={() => navigate(`/shops/${item.shopId}?tab=subscription`)}>
                          {item.shopName}
                        </button>
                        <div className="text-xs text-[#4B5563]">{item.ownerPhone || item.ownerEmail}</div>
                        <div className="mt-1 text-[11px] text-[#6B7280]">
                          {item.accessSource === "admin_grant" ? "Administrative grant" : `${item.billingCurrencyCode} billing`}
                        </div>
                      </TableCell>
                      <TableCell>
                        {canOperate && rowPlans.length ? (
                          <Select
                            value={item.planCode || ""}
                            disabled={isBusy}
                            onValueChange={(planCode) => {
                              const selected = rowPlans.find((plan) => plan.code === planCode);
                              setAction({ type: "plan", shopId: item.shopId, shopName: item.shopName, planCode, planName: selected?.name || planCode });
                            }}
                          >
                            <SelectTrigger className="h-9 w-44 rounded-lg bg-white"><SelectValue placeholder="Choose plan" /></SelectTrigger>
                            <SelectContent>
                              {rowPlans.map((plan) => (
                                <SelectItem key={plan.id} value={plan.code}>
                                  {plan.name} · {formatMoneyMinor(plan.priceMinor, plan.currencyCode)}
                                </SelectItem>
                              ))}
                            </SelectContent>
                          </Select>
                        ) : <span className="text-sm text-[#4B5563]">{item.planName}</span>}
                      </TableCell>
                      <TableCell><StatusBadge status={item.accessStatus} /></TableCell>
                      <TableCell>
                        <div className="text-sm font-medium text-[#064E3B]">{item.accessUntil ? fmtDate(item.accessUntil) : "Not scheduled"}</div>
                        {until !== null && <div className={`text-xs ${until < 0 ? "text-red-700" : until <= 7 ? "text-amber-700" : "text-[#6B7280]"}`}>{until < 0 ? `${Math.abs(until)}d overdue` : `in ${until}d`}</div>}
                      </TableCell>
                      <TableCell className="font-semibold text-[#064E3B]">
                        {item.mrrMinor == null ? "—" : formatMoneyMinor(item.mrrMinor, item.billingCurrencyCode)}
                      </TableCell>
                      <TableCell className="text-right">
                        {canOperate && (
                          <div className="flex justify-end gap-1.5">
                            <Button size="sm" variant="outline" disabled={isBusy} onClick={() => setAction({ type: "grant", shopId: item.shopId, shopName: item.shopName })}>
                              {item.accessSource === "admin_grant" ? "Extend 30d" : "Grant 30d"}
                            </Button>
                            <Button size="sm" variant="ghost" className="text-red-700" disabled={isBusy || item.accessStatus === "revoked"} onClick={() => setAction({ type: "revoke", shopId: item.shopId, shopName: item.shopName })}>
                              <ShieldOff className="h-3.5 w-3.5" />
                            </Button>
                          </div>
                        )}
                      </TableCell>
                    </TableRow>
                  );
                })}
              </TableBody>
            </Table>
          </div>
        </TabsContent>
      </Tabs>

      {(page > 0 || data?.nextCursor) && (
        <div className="mt-4 flex items-center justify-between text-xs text-[#4B5563]">
          <span>Page {page + 1} · 40 records per page</span>
          <div className="flex gap-2">
            <Button size="sm" variant="outline" disabled={page === 0} onClick={() => setPage((current) => current - 1)}><ArrowLeft className="mr-1 h-3.5 w-3.5" />Previous</Button>
            <Button size="sm" variant="outline" disabled={!data?.nextCursor} onClick={nextPage}>Next<ArrowRight className="ml-1 h-3.5 w-3.5" /></Button>
          </div>
        </div>
      )}

      <AlertDialog open={Boolean(action)} onOpenChange={(open) => { if (!open && !mutation.isPending) { setAction(null); setReason(""); } }}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>
              {action?.type === "plan" ? `Change ${action?.shopName} to ${action?.planName}?` : action?.type === "revoke" ? `Revoke ${action?.shopName}'s access?` : `Grant ${action?.shopName} 30 days?`}
            </AlertDialogTitle>
            <AlertDialogDescription>
              This change is authoritative, immediately visible to the shop, and recorded in the administrative audit log.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <Textarea value={reason} onChange={(event) => setReason(event.target.value)} maxLength={240} placeholder="Administrative reason (required)" />
          <AlertDialogFooter>
            <AlertDialogCancel disabled={mutation.isPending}>Cancel</AlertDialogCancel>
            <AlertDialogAction
              disabled={reason.trim().length < 3 || mutation.isPending}
              onClick={(event) => {
                event.preventDefault();
                mutation.mutate(action);
              }}
              className={action?.type === "revoke" ? "bg-red-600 hover:bg-red-700" : "bg-[#0E5B52] hover:bg-[#0A4640]"}
            >
              Confirm change
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
